import { FitnessProfileType, WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import { FitnessProfiles } from "@WahooFitness/cloud-client-ts";
import { useCloudContext, useConfigContext, useOfflineSWR } from "@WahooFitness/wahoo-offline-mfe";
import { useCallback, useMemo } from "react";

type FitnessProfileUpdate = Omit<
  FitnessProfileType,
  "user_id" | "dur_1_src" | "dur_2_src" | "dur_3_src" | "dur_4_src"
>;

const useFitnessProfiles = () => {
  const { wahooToken } = useConfigContext();
  const { getCloudClient } = useCloudContext();
  const fitnessProfilesClient = useMemo(() => getCloudClient(FitnessProfiles), [getCloudClient]);
  const {
    data: userFitnessProfilesData,
    isLoading: userFitnessProfilesIsLoading,
    error: userFitnessProfilesError,
    mutate: mutateUserFitnessProfile,
  } = useOfflineSWR(["userFitnessProfiles", wahooToken], ([_key, token]) =>
    fitnessProfilesClient.get(token)
  );

  const cyclingFitnessProfile = useMemo(
    () =>
      userFitnessProfilesData?.find(
        (profile) => profile.workout_type_family_id === WahooWorkoutTypeFamily.Cycling
      ),
    [userFitnessProfilesData]
  );

  const runningFitnessProfile = useMemo(
    () =>
      userFitnessProfilesData?.find(
        (profile) => profile.workout_type_family_id === WahooWorkoutTypeFamily.Running
      ),
    [userFitnessProfilesData]
  );

  const showMissingFitnessProfileBadge = useMemo(() => {
    if (userFitnessProfilesIsLoading || cyclingFitnessProfile || runningFitnessProfile)
      return false;
    return true;
  }, [runningFitnessProfile, userFitnessProfilesIsLoading, cyclingFitnessProfile]);

  const createOrUpdateFitnessProfile = useCallback(
    (update: FitnessProfileUpdate, existingId?: number) => {
      if (existingId) {
        return fitnessProfilesClient.updateFitnessProfile(
          existingId,
          update,
          wahooToken,
          existingId === cyclingFitnessProfile?.id
        );
      }
      return fitnessProfilesClient.createFitnessProfile(update, wahooToken);
    },
    [cyclingFitnessProfile?.id, fitnessProfilesClient, wahooToken]
  );

  return {
    userFitnessProfilesData,
    userFitnessProfilesIsLoading,
    userFitnessProfilesError,
    mutateUserFitnessProfile,
    cyclingFitnessProfile,
    runningFitnessProfile,
    showMissingFitnessProfileBadge,
    createOrUpdateFitnessProfile,
  };
};

export default useFitnessProfiles;
